import { initialEvidence } from "app/_pages/_healthSafetyEnvironment/IncidentManagement/CONSTANTS";
import { getAllAreaOfOperation } from "app/_services/ehs/ehsAreaOfOperationServices";
import { getAllTrainings } from "app/_services/ehs/ehsServices";
import { getAllSites } from "app/_services/ehs/ehsSiteServices";
import {
  getAllContractorPerson,
  getAllOldContPerson,
  getContPersonForOtherModuel,
} from "app/_services/UserManagement/ContractorPersonService";
import {
  getAllOldEmployee,
  getEmployee,
  getEmployeeForOtherModuel,
} from "app/_services/UserManagement/EmployeeService";
import { getAllCategory } from "app/_services/_globalConfiguration/Category/CategoryService";
import { useCallback, useEffect, useMemo, useState } from "react";
import { employeeDropdownLabel } from "./helper";
import { getContractorForOtherModuel } from "app/_services/Contractor/ContractorService";

export const debounce = (func, delay = 100) => {
  let debounceTimer;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
};

export const throttle = (func, delay = 100) => {
  // Previously called time of the function
  let prev = 0;
  return (...args) => {
    // Current called time of the function
    let now = new Date().getTime();

    // Logging the difference between previously
    // called and current called timings

    // If difference is greater than delay call
    // the function again.
    if (now - prev > delay) {
      prev = now;

      // "..." is the spread operator here
      // returning the function with the
      // array of arguments
      return func(...args);
    }
  };
};

export function useToggle(initialValue = false) {
  const [value, setValue] = useState(() => initialValue);

  const toggle = useCallback(
    (value) => setValue((prev) => (typeof value === "boolean" ? value : !prev)),
    []
  );

  return [value, toggle, setValue];
}

export function useForm(initialForm = {}, fields) {
  const [form, setForm] = useState(() => initialForm);

  const onChange = useCallback(
    (e) => {
      const event = { ...e };
      const { name, value, type, checked, files } = event.target;

      setForm((prev) => {
        const field = fields?.find((f) => (f.selectorForm || f.selector) === name);
        const extraToChange = field?.extraToChange?.(prev, event) || {};

        const newForm = {
          ...prev,
          [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
          ...extraToChange,
        };

        // "prev[name] !== value" Because Selected option could be the same as Previous, so No Need to reset!
        if (prev[name] !== value && field?.dependencies)
          field.dependencies.forEach((f) => {
            if (f.selector) newForm[f.selector] = "";
            if (f.selectorForm) newForm[f.selectorForm] = "";
          });

        return newForm;
      });
    },
    [fields]
  );

  return [form, onChange, setForm];
}

export function useDebounce(func = () => {}, dependencies = [], delay = 500) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funcToRun = useCallback(func, [...dependencies]);
  const debouncedFunction = useMemo(() => debounce(funcToRun, delay), [funcToRun, delay]);

  return debouncedFunction;
}
/*
You may first use 'useCallback' then change it to 'useDebounce' to validate the correct dependencies. Note: You must pass the component state in it's arguments and component props in the dependency array.
*/
export function useThrottle(func = () => {}, dependencies = [], delay = 500) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funcToRun = useCallback(func, [...dependencies]);
  const throttledFunction = useMemo(() => throttle(funcToRun, delay), [funcToRun, delay]);

  return throttledFunction;
}

export function useSite(filter) {
  const [sites, setSites] = useState([]);
  const [sitesOption, setSitesOption] = useState([]);

  useEffect(() => {
    getAllSites(filter || "filter[status]=1").then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setSites(data);
      setSitesOption(data.map((site) => ({ label: site.name, id: site.id })));
    });
  }, [filter]);

  return [sites, sitesOption];
}

export function useEmployee(filter) {
  const [employees, setEmployees] = useState([]);
  const [employeesOption, setEmployeesOption] = useState([]);
  // console.log("/*/*/**/employee", filter);
  useEffect(() => {
    getEmployee(filter || "filter[status]=1").then((response) => {
      // console.log("/*/*/**/employee", response);
      if (!response?.status) return;

      const data = response?.data?.employees || [];

      setEmployees(data);
      setEmployeesOption(
        data.map((employee) => ({
          label: employeeDropdownLabel(employee),
          id: employee.id,
          value: employee.id,
          siteType: employee?.ehsSite?.siteType,
          employmentStatus: employee?.employmentStatus,
        }))
      );
    });
  }, [filter]);

  return [employees, employeesOption];
}

export function useContractorPerson(filter) {
  const [contractorPersons, setContractorPersons] = useState([]);
  const [contractorPersonsOption, setContractorPersonsOption] = useState([]);

  useEffect(() => {
    getAllContractorPerson(filter || "filter[status]=1").then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setContractorPersons(data);
      setContractorPersonsOption(
        data.map((contractorPerson) => ({
          label: `${contractorPerson.firstName} ${contractorPerson.lastName}`,
          id: contractorPerson.id,
          value: contractorPerson.id,
          employmentStatus: contractorPerson?.employmentStatus,
        }))
      );
    });
  }, [filter]);

  return [contractorPersons, contractorPersonsOption];
}

export function useCategory(filter) {
  const [category, setCategory] = useState([]);
  const [categoryOption, setCategoryOption] = useState([]);

  useEffect(() => {
    getAllCategory(filter || "filter[status]=1").then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setCategory(data);
      setCategoryOption(
        data.map((categoryPerson) => ({
          label: `${categoryPerson.name}`,
          id: categoryPerson.id,
          value: categoryPerson.id,
        }))
      );
    });
  }, [filter]);

  return [category, categoryOption];
}

export function useArea(filter) {
  const [area, setArea] = useState([]);
  const [areaOption, setAreaOption] = useState([]);

  useEffect(() => {
    getAllAreaOfOperation(filter || "filter[status]=1").then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setArea(data);
      setAreaOption(
        data.map((areaPerson) => ({
          label: `${areaPerson.name}`,
          id: areaPerson.id,
          value: areaPerson.id,
        }))
      );
    });
  }, [filter]);

  return [area, areaOption];
}

export function useTraining(filter) {
  const [training, setTraining] = useState([]);
  const [trainingOption, setTrainingOption] = useState([]);

  useEffect(() => {
    getAllTrainings(filter || "filter[status]=1").then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setTraining(data);
      setTrainingOption(
        data.map((trainingPerson) => ({
          label: `${trainingPerson.name}`,
          id: trainingPerson.id,
          value: trainingPerson.id,
        }))
      );
    });
  }, [filter]);

  return [training, trainingOption];
}

export function useEvidence(extraToUpdate) {
  const [evidences, setEvidences] = useState([]);

  const addEvidence = useCallback(
    (id) =>
      setEvidences((previous) => [...previous, { ...initialEvidence, ...extraToUpdate }]),
    [extraToUpdate]
  );

  const deleteEvidence = useCallback(
    (index) =>
      setEvidences((previous) =>
        previous
          .map((data, idx) =>
            idx !== index ? data : data.id ? { ...data, deleted: 1 } : null
          )
          .filter((data) => !!data)
      ),
    []
  );

  const onChangeEvidence = useCallback(({ name, value, type, checked, index }) => {
    setEvidences((prevEvidences) =>
      prevEvidences.map((data, idx) => {
        if (idx !== index) return data;

        data[name] = type === "checkbox" ? checked : value;
        return data;
      })
    );
  }, []);

  const onChangeEvidenceFile = useCallback(({ responseData, index }) => {
    setEvidences((prevEvidences) =>
      prevEvidences.map((data, idx) => {
        if (idx !== index) return data;

        data.file = responseData;
        data.fileId = responseData.id;

        return data;
      })
    );
  }, []);

  return [
    evidences,
    setEvidences,
    addEvidence,
    deleteEvidence,
    onChangeEvidence,
    onChangeEvidenceFile,
  ];
}

export function useEmployeeDropdown(isOldEmp = false) {
  const [employees, setEmployees] = useState([]);
  const [employeesOption, setEmployeesOption] = useState([]);
  // console.log("/*/*/**/employee", filter);
  useEffect(() => {
    (isOldEmp ? getAllOldEmployee() : getEmployeeForOtherModuel()).then((response) => {
      // console.log("/*/*/**/employee", response);
      if (!response?.status) return;

      const data = response?.data || [];

      setEmployees(data);
      setEmployeesOption(
        data.map((employee) => ({
          label: employeeDropdownLabel(employee),
          id: employee.id,
          value: employee.id,
          siteType: employee?.ehsSite?.siteType,
          employmentStatus: employee?.employmentStatus,
        }))
      );
    });
  }, [isOldEmp]);

  return [employees, employeesOption];
}

export function useContractorPersonDropdown(isOldConPerson = false) {
  const [contractorPersons, setContractorPersons] = useState([]);
  const [contractorPersonsOption, setContractorPersonsOption] = useState([]);

  useEffect(() => {
    (isOldConPerson ? getAllOldContPerson() : getContPersonForOtherModuel()).then(
      (response) => {
        if (!response?.status) return;

        const data = response?.data || [];

        setContractorPersons(data);
        setContractorPersonsOption(
          data.map((contractorPerson) => ({
            label: `${contractorPerson.firstName} ${contractorPerson.lastName}`,
            id: contractorPerson.id,
            value: contractorPerson.id,
            employmentStatus: contractorPerson?.employmentStatus,
          }))
        );
      }
    );
  }, [isOldConPerson]);

  return [contractorPersons, contractorPersonsOption];
}

export function useContractorDropdown() {
  const [contractors, setContractors] = useState([]);
  const [contractorOption, setContractorOption] = useState([]);

  useEffect(() => {
    getContractorForOtherModuel().then((response) => {
      if (!response?.status) return;

      const data = response?.data || [];

      setContractors(data);
      setContractorOption(
        data.map((contractor) => ({
          label: `${contractor.contractorLegalEntity}`,
          id: contractor.id,
          value: contractor.id,
        }))
      );
    });
  }, []);

  return [contractors, contractorOption];
}
